import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Center,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Text,
    VStack,
    CircularProgress,
    Spinner,
} from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, Eye, EyeOff, Lock, XCircle } from "lucide-react";
import { supabase } from "./utils/supabaseClient";

// ─── Password strength helpers ────────────────────────────────────────────────

const getStrengthScore = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
};

const STRENGTH_META = [
    { label: "Weak", color: "#FC8181" },        // score 1 – red
    { label: "Fair", color: "#F6AD55" },         // score 2 – orange
    { label: "Good", color: "#ECC94B" },         // score 3 – yellow
    { label: "Strong", color: "#68D391" },       // score 4 – green
    { label: "Very strong", color: "#38A169" },  // score 5 – emerald
];

const PasswordStrengthBar = ({ password }) => {
    const score = getStrengthScore(password);
    if (!password) return null;

    const meta = STRENGTH_META[score - 1] || STRENGTH_META[0];

    return (
        <Box mt={2}>
            <Box display="flex" gap={1} mb={1}>
                {[1, 2, 3, 4, 5].map((seg) => (
                    <Box
                        key={seg}
                        flex={1}
                        h="4px"
                        borderRadius="full"
                        bg={seg <= score ? meta.color : "gray.200"}
                        transition="background 0.3s"
                    />
                ))}
            </Box>
            <Text fontSize="xs" color={meta.color} fontWeight="semibold" textAlign="right">
                {meta.label}
            </Text>
        </Box>
    );
};

// ─── Main component ────────────────────────────────────────────────────────────

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [pageState, setPageState] = useState("verifying"); // verifying | error | ready | success
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoURL, setLogoURL] = useState(null);

    // Fetch logo
    useEffect(() => {
        const getOrganisationLogo = async () => {
            const { data } = await supabase
                .from("organization")
                .select("logo_url")
                .single();
            if (data) setLogoURL(data.logo_url);
        };
        getOrganisationLogo();
    }, []);

    // Exchange code on mount
    useEffect(() => {
        const exchangeCode = async () => {
            const code = searchParams.get("code");
            if (!code) {
                setErrorMessage("No reset code found in the link. Please request a new one.");
                setPageState("error");
                return;
            }
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
                setErrorMessage(error.message || "This link has expired or is invalid.");
                setPageState("error");
            } else {
                setPageState("ready");
            }
        };
        exchangeCode();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-redirect after success
    useEffect(() => {
        if (pageState === "success") {
            const timer = setTimeout(() => navigate("/login"), 3000);
            return () => clearTimeout(timer);
        }
    }, [pageState, navigate]);

    const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
    const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;
    const canSubmit = password.length > 0 && passwordsMatch && !isSubmitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setIsSubmitting(true);
        const { error } = await supabase.auth.updateUser({ password });
        if (error) {
            setErrorMessage(error.message || "Failed to update password. Please try again.");
            setPageState("error");
        } else {
            await supabase.auth.signOut();
            setPageState("success");
        }
        setIsSubmitting(false);
    };

    return (
        <Box
            h="100vh"
            w="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            px={{ base: 4, md: 0 }}
            bg={{ base: "gray.50", md: "white" }}
            overflow="hidden"
            position="relative"
        >
            <Container maxW="container.sm" w="100%" px={{ base: 0, md: 4 }} mt={{ base: -8, md: -12 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <Box
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="lg"
                        boxShadow={{ base: "sm", md: "md" }}
                        textAlign="center"
                        w="100%"
                    >
                        <VStack spacing={5} align="center">
                            {/* Logo */}
                            {logoURL && (
                                <motion.div
                                    initial={{ y: -50 }}
                                    animate={{ y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
                                >
                                    <Image
                                        src={logoURL}
                                        alt="Logo"
                                        boxSize={{ base: "120px", md: "160px" }}
                                        objectFit="contain"
                                        mb={0}
                                    />
                                </motion.div>
                            )}

                            {pageState === "verifying" && <VerifyingState />}
                            {pageState === "error" && <ErrorState message={errorMessage} />}
                            {pageState === "ready" && (
                                <ReadyState
                                    password={password}
                                    setPassword={setPassword}
                                    confirmPassword={confirmPassword}
                                    setConfirmPassword={setConfirmPassword}
                                    showPassword={showPassword}
                                    setShowPassword={setShowPassword}
                                    showConfirm={showConfirm}
                                    setShowConfirm={setShowConfirm}
                                    passwordsMatch={passwordsMatch}
                                    passwordsMismatch={passwordsMismatch}
                                    canSubmit={canSubmit}
                                    isSubmitting={isSubmitting}
                                    onSubmit={handleSubmit}
                                />
                            )}
                            {pageState === "success" && <SuccessState />}
                        </VStack>
                    </Box>

                    {/* Back to login (shown on error and ready states) */}
                    {(pageState === "error" || pageState === "ready") && (
                        <Center mt={6}>
                            <Link to="/login" style={{ textDecoration: "none" }}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    cursor="pointer"
                                >
                                    <ArrowLeft size={15} color="#718096" />
                                    <Text fontSize="sm" color="gray.500">Back to login</Text>
                                </Box>
                            </Link>
                        </Center>
                    )}
                </motion.div>
            </Container>
        </Box>
    );
};

// ─── Sub-states ────────────────────────────────────────────────────────────────

const VerifyingState = () => (
    <>
        <Spinner size="lg" color="gray.400" thickness="3px" />
        <Text fontSize="sm" color="gray.500">Verifying link…</Text>
    </>
);

const ErrorState = ({ message }) => (
    <>
        <Center
            w="64px"
            h="64px"
            borderRadius="full"
            bg="red.50"
            border="2px solid"
            borderColor="red.200"
        >
            <XCircle size={30} color="#FC8181" />
        </Center>

        <Heading size="sm" color="gray.800">Link expired or invalid</Heading>

        <Text fontSize="sm" color="gray.500" textAlign="center" maxW="320px">
            {message}
        </Text>

        <VStack spacing={3} w="100%">
            <Link to="/forgot-password" style={{ width: "100%" }}>
                <Button
                    width="full"
                    height={{ base: "48px", md: "44px" }}
                    color="white"
                    bg="gray.800"
                    _hover={{ bg: "gray.700" }}
                    _active={{ bg: "gray.900" }}
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="semibold"
                >
                    Request a new reset link
                </Button>
            </Link>
            <Link to="/login" style={{ width: "100%" }}>
                <Button
                    width="full"
                    height={{ base: "44px", md: "40px" }}
                    variant="outline"
                    borderColor="gray.300"
                    color="gray.700"
                    _hover={{ bg: "gray.50" }}
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="semibold"
                >
                    Back to login
                </Button>
            </Link>
        </VStack>
    </>
);

const ReadyState = ({
    password, setPassword, confirmPassword, setConfirmPassword,
    showPassword, setShowPassword, showConfirm, setShowConfirm,
    passwordsMatch, passwordsMismatch, canSubmit, isSubmitting, onSubmit,
}) => (
    <>
        <Center
            w="64px"
            h="64px"
            borderRadius="full"
            bg="gray.100"
            border="2px solid"
            borderColor="gray.200"
        >
            <Lock size={28} color="#4A5568" />
        </Center>

        <Heading size="sm">Set a new password</Heading>

        <Box as="form" onSubmit={onSubmit} w="100%" textAlign="left">
            <VStack spacing={4} align="stretch">
                {/* New password */}
                <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }} mb={2}>
                        New password
                    </FormLabel>
                    <InputGroup size={{ base: "md", md: "lg" }}>
                        <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            bg="white"
                            fontSize={{ base: "16px", md: "md" }}
                            minH={{ base: "48px", md: "40px" }}
                            autoComplete="new-password"
                            pr="3rem"
                        />
                        <InputRightElement h="full">
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                onClick={() => setShowPassword((v) => !v)}
                                color="gray.400"
                                _hover={{ color: "gray.600", bg: "transparent" }}
                            />
                        </InputRightElement>
                    </InputGroup>
                    <PasswordStrengthBar password={password} />
                </FormControl>

                {/* Confirm password */}
                <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }} mb={2}>
                        Confirm new password
                    </FormLabel>
                    <InputGroup size={{ base: "md", md: "lg" }}>
                        <Input
                            type={showConfirm ? "text" : "password"}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            bg="white"
                            fontSize={{ base: "16px", md: "md" }}
                            minH={{ base: "48px", md: "40px" }}
                            autoComplete="new-password"
                            pr="3rem"
                            borderColor={
                                passwordsMatch ? "green.400" :
                                passwordsMismatch ? "red.400" : undefined
                            }
                            _focus={{
                                borderColor: passwordsMatch ? "green.400" :
                                    passwordsMismatch ? "red.400" : "blue.300",
                            }}
                        />
                        <InputRightElement h="full">
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label={showConfirm ? "Hide password" : "Show password"}
                                icon={showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                onClick={() => setShowConfirm((v) => !v)}
                                color="gray.400"
                                _hover={{ color: "gray.600", bg: "transparent" }}
                            />
                        </InputRightElement>
                    </InputGroup>
                    {passwordsMismatch && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                            Passwords do not match
                        </Text>
                    )}
                    {passwordsMatch && (
                        <Text fontSize="xs" color="green.500" mt={1}>
                            Passwords match
                        </Text>
                    )}
                </FormControl>

                <Button
                    type="submit"
                    mt={2}
                    width="full"
                    height={{ base: "48px", md: "44px" }}
                    color="white"
                    bg="gray.800"
                    _hover={{ bg: "gray.700" }}
                    _active={{ bg: "gray.900" }}
                    isLoading={isSubmitting}
                    isDisabled={!canSubmit}
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="semibold"
                    loadingText="Updating…"
                >
                    Update password
                </Button>
            </VStack>
        </Box>
    </>
);

const SuccessState = () => (
    <>
        <Center
            w="64px"
            h="64px"
            borderRadius="full"
            bg="green.50"
            border="2px solid"
            borderColor="green.200"
        >
            <CheckCircle size={30} color="#38A169" />
        </Center>

        <Heading size="sm">Password updated!</Heading>

        <Text fontSize="sm" color="gray.500" textAlign="center">
            Your password has been changed successfully.
        </Text>

        <Box display="flex" alignItems="center" gap={2} color="gray.400">
            <CircularProgress isIndeterminate size="18px" color="gray.400" />
            <Text fontSize="sm" color="gray.400">Redirecting to login…</Text>
        </Box>
    </>
);

export default ResetPassword;
